import { useState } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { UWStatsFigures } from "@/models/underwriting/DashboardStats";

function Trend({ pct }: { pct?: number | null }) {
  if (pct === undefined || pct === null) return null;
  const Icon = pct >= 0 ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="flex items-center gap-0.5 text-xs font-medium tabular-nums text-muted-foreground">
      <Icon className="size-3.5" />
      {Math.abs(pct).toFixed(1)}%
    </span>
  );
}

interface StatCardProps {
  title: string;
  value: React.ReactNode;
  trendPct?: number;
  className?: string;
}

export function StatCard({ title, value, trendPct, className }: StatCardProps) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1 rounded-xl border border-border bg-card px-4 py-3 shadow-sm",
        className
      )}
    >
      <p className="text-sm font-medium text-muted-foreground">{title}</p>
      <p className="text-3xl font-medium leading-none text-foreground">{value}</p>
      <Trend pct={trendPct} />
    </div>
  );
}

const OUTCOME_ROWS: { key: "approvedCount" | "declinedCount" | "alternativeCount" | "moreInfoCount"; label: string }[] = [
  { key: "approvedCount", label: "Approved" },
  { key: "declinedCount", label: "Declined" },
  { key: "alternativeCount", label: "Alternative" },
  { key: "moreInfoCount", label: "More Info" },
];

function DecisionOutcomesCard({ stats }: { stats: UWStatsFigures }) {
  const [mode, setMode] = useState<"count" | "percent">("count");
  const counts = {
    approvedCount: stats.approvedCount ?? 0,
    declinedCount: stats.declinedCount ?? 0,
    alternativeCount: stats.alternativeCount ?? 0,
    moreInfoCount: stats.moreInfoCount ?? 0,
  };
  const total =
    counts.approvedCount + counts.declinedCount + counts.alternativeCount + counts.moreInfoCount;

  return (
    <div className="flex flex-col rounded-xl border border-border bg-card px-4 py-3 shadow-sm">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium text-foreground">Decision Outcomes</p>
        <div className="flex items-center gap-0.5 rounded-full bg-muted p-0.5">
          {(["count", "percent"] as const).map((nextMode) => (
            <button
              key={nextMode}
              onClick={() => setMode(nextMode)}
              className={cn(
                "rounded-full px-2 py-0.5 text-xs font-medium transition-colors",
                mode === nextMode
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              {nextMode === "count" ? "#" : "%"}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-2 flex flex-col divide-y divide-border">
        {OUTCOME_ROWS.map(({ key, label }) => {
          const value = counts[key];
          const display = mode === "count" ? value : `${total > 0 ? Math.round((value / total) * 100) : 0}%`;
          return (
            <div key={key} className="flex items-center justify-between py-2 text-sm">
              <span className="text-muted-foreground">{label}</span>
              <span className="font-semibold tabular-nums text-foreground">{display}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function formatTurnaround(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes <= 0) return "—";
  if (minutes < 60) return `${Math.round(minutes)}mins`;
  const hours = minutes / 60;
  const displayHours = Number.isInteger(hours) ? `${hours}` : hours.toFixed(1);
  return `${displayHours}hrs`;
}

export function StatCardsSection({ stats }: { stats: UWStatsFigures }) {
  const decisions =
    stats.decisionsCount ??
    (stats.approvedCount ?? 0) +
      (stats.declinedCount ?? 0) +
      (stats.alternativeCount ?? 0) +
      (stats.moreInfoCount ?? 0);
  const completionRate =
    stats.completionRatePct ??
    (stats.totalRequestsInPeriod > 0
      ? Math.round((decisions / stats.totalRequestsInPeriod) * 1000) / 10
      : 0);

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-4 lg:items-stretch">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 lg:col-span-3">
        <StatCard title="Requests" value={stats.totalRequestsInPeriod} trendPct={stats.requestsTrendPct} />
        <StatCard title="Pending" value={stats.pendingCount} trendPct={stats.pendingTrendPct} />
        <StatCard title="Decisions" value={decisions} trendPct={stats.decisionsTrendPct} />
        <StatCard
          title="Turnaround"
          value={formatTurnaround(stats.avgTurnaroundMinutes)}
          trendPct={stats.turnaroundTrendPct}
        />
        <StatCard title="Completion Rate" value={`${completionRate}%`} trendPct={stats.completionRateTrendPct} />
        <StatCard
          title="Resubmission Rate"
          value={`${stats.resubmissionRatePct}%`}
          trendPct={stats.resubmissionRateTrendPct}
        />
      </div>
      <DecisionOutcomesCard stats={stats} />
    </div>
  );
}